import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import update from 'react-addons-update'
import Modal from 'react-modal'
import { FormattedMessage, injectIntl } from 'react-intl'
import ModalPinCode from '../../partials/SettingPageComponents/ModalPinCode'
import { isNumber, PinInput } from '../../partials/Inputs/Inputs'

import styles from '../../../../css/notifications.module.css'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

Modal.setAppElement('#app')

const PreferencePinCode = ({ user, pinCode, backToUser, intl }) => {
  const goBackOnceRef = useRef(false)
  const switchOnceRef = useRef(false)
  const newIn1FocusRef = useRef(null)
  const newIn2FocusRef = useRef(null)
  const newIn3FocusRef = useRef(null)
  const newIn4FocusRef = useRef(null)
  const newAgainIn1FocusRef = useRef(null)
  const newAgainIn2FocusRef = useRef(null)
  const newAgainIn3FocusRef = useRef(null)
  const newAgainIn4FocusRef = useRef(null)
  const prevModalNewOpenRef = useRef(false)

  const {
    formatMessage,
  } = intl

  const [state, setState] = useState({
    newIn1: '',
    newIn2: '',
    newIn3: '',
    newIn4: '',
    newPin: '0000',
    newAgainIn1: '',
    newAgainIn2: '',
    newAgainIn3: '',
    newAgainIn4: '',
    newAgainPin: '0000',
    invalid: true,
    modalNewOpen: false,
    modalNewAgainOpen: false,
    inputStyle: styles.pinCodeNumberInput,
    newPinMessage: formatMessage({
      id: 'pinCode.enterNew',
      defaultMessage: 'Enter New Pin Code',
      description: '',
    }),
  })

  const inputFocus = useCallback((input) => {
    switch (input) {
      case 'newIn1':
        newIn1FocusRef.current?.focusInput()
        break
      case 'newIn2':
        newIn2FocusRef.current?.focusInput()
        break
      case 'newIn3':
        newIn3FocusRef.current?.focusInput()
        break
      case 'newIn4':
        newIn4FocusRef.current?.focusInput()
        break
      case 'newAgainIn1':
        newAgainIn1FocusRef.current?.focusInput()
        break
      case 'newAgainIn2':
        newAgainIn2FocusRef.current?.focusInput()
        break
      case 'newAgainIn3':
        newAgainIn3FocusRef.current?.focusInput()
        break
      case 'newAgainIn4':
        newAgainIn4FocusRef.current?.focusInput()
        break
      default:
    }
  }, [])

  const assemblePin = useCallback((currentState, callback) => {
    let newPin = ''
    newPin += (isNumber(currentState.newIn1) ? currentState.newIn1 : 0).toString()
    newPin += (isNumber(currentState.newIn2) ? currentState.newIn2 : 0).toString()
    newPin += (isNumber(currentState.newIn3) ? currentState.newIn3 : 0).toString()
    newPin += (isNumber(currentState.newIn4) ? currentState.newIn4 : 0).toString()

    let newAgainPin = ''
    newAgainPin += (isNumber(currentState.newAgainIn1) ? currentState.newAgainIn1 : 0).toString()
    newAgainPin += (isNumber(currentState.newAgainIn2) ? currentState.newAgainIn2 : 0).toString()
    newAgainPin += (isNumber(currentState.newAgainIn3) ? currentState.newAgainIn3 : 0).toString()
    newAgainPin += (isNumber(currentState.newAgainIn4) ? currentState.newAgainIn4 : 0).toString()

    const newState = update(currentState, {
      newPin: { $set: newPin },
      newAgainPin: { $set: newAgainPin },
    })

    setState(newState)

    if (callback) {
      setTimeout(callback, 0)
    }

    return newState
  }, [])

  const switchModals = useCallback((callback) => {
    setState(prevState => {
      const newState = {
        modalNewOpen: false,
        modalNewAgainOpen: true,
      }
      if (prevState.modalNewOpen === false) {
        newState.modalNewOpen = true
        newState.modalNewAgainOpen = false
      }

      const updatedState = {
        ...prevState,
        ...newState
      }

      if (callback) {
        setTimeout(callback, 0)
      }

      return updatedState
    })
  }, [])

  const appDispatch = useCallback((newPinValue) => {
    AppDispatcher.dispatch({
      action: 'update-user-pin',
      oldPin: pinCode.code.length > 0 ? pinCode.code : '0',
      newPin: newPinValue,
    })
  }, [pinCode.code])

  const resetInputs = useCallback((callback, message) => {
    setState({
      newIn1: '',
      newIn2: '',
      newIn3: '',
      newIn4: '',
      newPin: '0000',
      newAgainIn1: '',
      newAgainIn2: '',
      newAgainIn3: '',
      newAgainIn4: '',
      newAgainPin: '0000',
      inputStyle: styles.pinCodeNumberInputReset,
      modalNewOpen: true,
      modalNewAgainOpen: false,
      invalid: true,
      newPinMessage: message || formatMessage({
        id: 'pinCode.errorNoMatch',
        defaultMessage: 'Pins Did Not Match, Please Try Again',
        description: '',
      }),
    })

    setTimeout(() => {
      inputFocus('newAgainIn1')
      setTimeout(() => {
        setState(prevState => ({
          ...prevState,
          inputStyle: styles.pinCodeNumberInput,
        }))
      }, 1000)
      if (callback) callback()
    }, 0)
  }, [formatMessage, inputFocus])

  const isValidPin = useCallback((pinValue) => (
    typeof pinValue === 'string' && /^\d{4}$/.test(pinValue)
  ), [])

  const verifyAndSendPinCode = useCallback((newPinValue, newAgainPinValue) => {
    if (!isValidPin(newPinValue) || !isValidPin(newAgainPinValue)) {
      resetInputs(undefined, formatMessage({
        id: 'pinCode.errorLength',
        defaultMessage: 'Pin must be 4 digits',
        description: '',
      }))
    } else if (newPinValue === newAgainPinValue) {
      appDispatch(newPinValue)
    } else {
      resetInputs()
    }
  }, [appDispatch, formatMessage, isValidPin, resetInputs])

  const handleChange = useCallback((field, value, callback) => {
    if (
      isNumber(value) &&
      Number(value) >= 0 &&
      Number(value) <= 9
    ) {
      const valueNumber = Number(value)
      setState(prevState => {
        const newState = update(prevState, {
          [field]: { $set: valueNumber },
        })

        const updatedState = assemblePin(newState, () => {
          switch (field) {
            case 'newIn1':
              inputFocus('newIn2')
              break
            case 'newIn2':
              inputFocus('newIn3')
              break
            case 'newIn3':
              inputFocus('newIn4')
              break
            case 'newIn4':
              switchModals()
              break
            case 'newAgainIn1':
              inputFocus('newAgainIn2')
              break
            case 'newAgainIn2':
              inputFocus('newAgainIn3')
              break
            case 'newAgainIn3':
              inputFocus('newAgainIn4')
              break
            case 'newAgainIn4':
              verifyAndSendPinCode(updatedState.newPin, updatedState.newAgainPin)
              break
            default:
              inputFocus(field)
          }
          if (callback) callback()
        })

        return updatedState
      })
    } else if (value === '') {
      setState(prevState => {
        const newState = update(prevState, {
          [field]: { $set: '' },
        })

        assemblePin(newState)
        if (callback) callback()

        return newState
      })
    }
  }, [assemblePin, inputFocus, switchModals, verifyAndSendPinCode])

  const childChange = useCallback((event) => {
    handleChange(event.target.name, event.target.value)
  }, [handleChange])

  const closeModal = useCallback(() => {
    backToUser()
  }, [backToUser])

  useEffect(() => {
    if (!user.ready) {
      backToUser()
    }
  }, [user.ready, backToUser])

  useEffect(() => {
    if (
      typeof user.data.permissions.pin === 'undefined'
    ) {
      switchModals()
      switchOnceRef.current = true
    }
  }, [switchModals])

  useEffect(() => {
    if (
      user.saved === true &&
      goBackOnceRef.current === false
    ) {
      goBackOnceRef.current = true
      AppDispatcher.dispatch({
        action: 'get-user',
        userId: user.id,
      })
      setTimeout(() => {
        backToUser()
      }, 1000)
    }
    if (
      user.saved === 'error' &&
      goBackOnceRef.current === false
    ) {
      goBackOnceRef.current = true
      setTimeout(() => {
        backToUser()
      }, 5000)
    }
  }, [user.saved, user.id, backToUser])

  useEffect(() => {
    if (
      pinCode.cancelled === true &&
      goBackOnceRef.current === false
    ) {
      goBackOnceRef.current = true
      backToUser()
    }
    if (
      pinCode.status === 'authenticated' &&
      switchOnceRef.current === false
    ) {
      switchModals()
      switchOnceRef.current = true
    }
  }, [pinCode.cancelled, pinCode.status, backToUser, switchModals])

  useEffect(() => {
    if (state.modalNewOpen && !prevModalNewOpenRef.current && newIn1FocusRef.current) {
      inputFocus('newIn1')
    } else if (!state.modalNewOpen && prevModalNewOpenRef.current && newAgainIn1FocusRef.current) {
      inputFocus('newAgainIn1')
    }
    prevModalNewOpenRef.current = state.modalNewOpen
  }, [state.modalNewOpen, inputFocus])

  useEffect(() => {
    return () => {
      AppDispatcher.dispatch({
        action: 'unload-user-change',
      })
      AppDispatcher.dispatch({
        action: 'pin-code-unload',
      })
    }
  }, [])

  let pinRequired = false
  if (
    user.ready &&
    typeof user.data.permissions.pin === 'string' &&
    pinCode.status !== 'authenticated'
  ) {
    pinRequired = true
  }

  let saveState
  if (user.saved === 'error') {
    saveState = (
      <div>
        <h3 className={styles.saveError}>
          <FormattedMessage
            id='pinCode.errorSavingToServer'
            defaultMessage='Error saving to server. Please try again later.'
            description='Pin Code error saving'
          />
        </h3>
      </div>
    )
  } else if (user.saved === true) {
    saveState = (
      <div>
        <h3 className={styles.saveSuccess}>
          <FormattedMessage
            id='pinCode.changeSuccessful'
            defaultMessage='Pin Code Changed Successfully'
            description='Pin Code Changed Successfully'
          />
        </h3>
      </div>
    )
  }

  return (
    <div>
      <ModalPinCode
        required={pinRequired}
        status={pinCode.status}
      />
      <Modal
        isOpen={state.modalNewOpen}
        onRequestClose={closeModal}
        className={{
          base: styles.pinCodeModalContent,
          afterOpen: styles.pinCodeModalContent_after_open,
          beforeClose: styles.pinCodeModalContent_before_close,
        }}
        overlayClassName={{
          base: styles.pinCodeModalOverlay,
          afterOpen: styles.pinCodeModalOverlay_after_open,
          beforeClose: styles.pinCodeModalOverlay_before_close,
        }}
        contentLabel='pin_code_modal_new'
      >
        <div className={styles.pinCodeModalContainer}>
          <button
            onClick={closeModal}
            className={`fa fa-times fa-2x ${styles.pinCodeExit}`}
            aria-hidden='true'
          />
          <div className={styles.pinCodeContainer}>
            <h3>
              {state.newPinMessage}
            </h3>
            <h3>
              <PinInput
                ref={newIn1FocusRef}
                value={state.newIn1}
                field='newIn1'
                childChange={childChange}
                style={state.inputStyle}
                autoFocus
              />
              <PinInput
                ref={newIn2FocusRef}
                value={state.newIn2}
                field='newIn2'
                childChange={childChange}
                style={state.inputStyle}
              />
              <PinInput
                ref={newIn3FocusRef}
                value={state.newIn3}
                field='newIn3'
                childChange={childChange}
                style={state.inputStyle}
              />
              <PinInput
                ref={newIn4FocusRef}
                value={state.newIn4}
                field='newIn4'
                childChange={childChange}
                style={state.inputStyle}
              />
            </h3>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={state.modalNewAgainOpen}
        onRequestClose={closeModal}
        className={{
          base: styles.pinCodeModalContent,
          afterOpen: styles.pinCodeModalContent_after_open,
          beforeClose: styles.pinCodeModalContent_before_close,
        }}
        overlayClassName={{
          base: styles.pinCodeModalOverlay,
          afterOpen: styles.pinCodeModalOverlay_after_open,
          beforeClose: styles.pinCodeModalOverlay_before_close,
        }}
        contentLabel='pin_code_modal_new_again'
      >
        <div className={styles.pinCodeModalContainer}>
          <button
            onClick={closeModal}
            className={`fa fa-times fa-2x ${styles.pinCodeExit}`}
            aria-hidden='true'
          />
          <div className={styles.pinCodeContainer}>
            <FormattedMessage
              id='pinCode.verify'
              defaultMessage='Verify New Pin Code'
              description='Verify New Pin Code'
              tagName='h3'
            />
            <h3>
              <PinInput
                ref={newAgainIn1FocusRef}
                value={state.newAgainIn1}
                field='newAgainIn1'
                childChange={childChange}
                style={state.inputStyle}
                autoFocus
              />
              <PinInput
                ref={newAgainIn2FocusRef}
                value={state.newAgainIn2}
                field='newAgainIn2'
                childChange={childChange}
                style={state.inputStyle}
              />
              <PinInput
                ref={newAgainIn3FocusRef}
                value={state.newAgainIn3}
                field='newAgainIn3'
                childChange={childChange}
                style={state.inputStyle}
              />
              <PinInput
                ref={newAgainIn4FocusRef}
                value={state.newAgainIn4}
                field='newAgainIn4'
                childChange={childChange}
                style={state.inputStyle}
              />
            </h3>
            {saveState}
          </div>
        </div>
      </Modal>
    </div>
  )
}

PreferencePinCode.propTypes = {
  user: PropTypes.object.isRequired,
  pinCode: PropTypes.object.isRequired,
  backToUser: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(PreferencePinCode)
